/**
 * InvestmentChatPanel
 * ====================
 * A slide-out chat drawer (right side) that connects to the LangGraph
 * Investment Manager Agent.  Features:
 *   - Persistent chat history loaded from the server on open
 *   - Session list to resume prior conversations
 *   - New conversation button
 *   - Markdown-style rendering of agent responses
 *   - Loading indicator while agent is thinking
 *   - Character counter / 4,000-char limit enforcement
 *   - Fully keyboard accessible
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const API_BASE = '/api/investment-agent';
const MAX_CHARS = 4000;
const PANEL_WIDTH = 420;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function periodLabel(period) {
    const map = {
        chat: 'Chat', daily: 'Daily', weekly: 'Weekly',
        monthly: 'Monthly', quarterly: 'Quarterly',
        semi: 'Semi-Annual', annual: 'Annual',
    };
    return map[period] || period;
}

/**
 * Very lightweight markdown renderer — handles **bold**, *italic*,
 * `code`, numbered lists, bullet lists, and line breaks.
 */
function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let key = 0;

    lines.forEach((line) => {
        // Headings
        if (line.startsWith('### ')) {
            elements.push(<h4 key={key++} style={styles.mdH4}>{line.slice(4)}</h4>);
            return;
        }
        if (line.startsWith('## ')) {
            elements.push(<h3 key={key++} style={styles.mdH3}>{line.slice(3)}</h3>);
            return;
        }
        if (line.startsWith('# ')) {
            elements.push(<h3 key={key++} style={styles.mdH3}>{line.slice(2)}</h3>);
            return;
        }
        // Horizontal rule
        if (line.trim() === '---' || line.trim() === '***') {
            elements.push(<hr key={key++} style={styles.mdHr} />);
            return;
        }
        // Bullet list
        if (line.startsWith('- ') || line.startsWith('* ')) {
            elements.push(
                <div key={key++} style={styles.mdBullet}>
                    <span style={styles.mdBulletDot}>•</span>
                    <span>{inlineFormat(line.slice(2))}</span>
                </div>
            );
            return;
        }
        // Numbered list
        const numMatch = line.match(/^(\d+)\.\s(.+)/);
        if (numMatch) {
            elements.push(
                <div key={key++} style={styles.mdBullet}>
                    <span style={styles.mdBulletDot}>{numMatch[1]}.</span>
                    <span>{inlineFormat(numMatch[2])}</span>
                </div>
            );
            return;
        }
        // Empty line → spacer
        if (line.trim() === '') {
            elements.push(<div key={key++} style={{ height: 6 }} />);
            return;
        }
        // Normal paragraph
        elements.push(<p key={key++} style={styles.mdP}>{inlineFormat(line)}</p>);
    });

    return <>{elements}</>;
}

function inlineFormat(text) {
    // Split on **bold**, *italic*, `code`
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={i}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
            return <code key={i} style={styles.mdCode}>{part.slice(1, -1)}</code>;
        }
        return part;
    });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function MessageBubble({ msg }) {
    const isUser = msg.role === 'user';
    return (
        <div style={{ ...styles.bubbleRow, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
            {!isUser && (
                <div style={styles.agentAvatar} title="Investment Manager Agent">🤖</div>
            )}
            <div style={isUser ? styles.userBubble : styles.agentBubble}>
                {isUser
                    ? <span style={styles.mdP}>{msg.content}</span>
                    : renderMarkdown(msg.content)
                }
                <div style={styles.bubbleTime}>{formatTime(msg.timestamp)}</div>
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div style={{ ...styles.bubbleRow, justifyContent: 'flex-start' }}>
            <div style={styles.agentAvatar}>🤖</div>
            <div style={{ ...styles.agentBubble, padding: '10px 14px' }}>
                <span style={styles.typingDot} />
                <span style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                <span style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function InvestmentChatPanel({ isOpen, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [threadId, setThreadId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sessions, setSessions] = useState([]);
    const [showSessions, setShowSessions] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Scroll to bottom whenever messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Focus input when panel opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 150);
            loadSessions();
        }
    }, [isOpen]);

    const loadSessions = async () => {
        try {
            const res = await axios.get(`${API_BASE}/sessions/?limit=15`);
            setSessions(res.data.sessions || []);
        } catch (e) {
            // non-critical
        }
    };

    const loadHistory = useCallback(async (tid) => {
        setLoadingHistory(true);
        setError('');
        try {
            const res = await axios.get(`${API_BASE}/chat/history/?thread_id=${tid}&limit=200`);
            setMessages(res.data.messages || []);
            setThreadId(tid);
            setShowSessions(false);
        } catch (e) {
            setError('Could not load conversation history.');
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const startNewConversation = () => {
        setThreadId(null);
        setMessages([]);
        setError('');
        setShowSessions(false);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;
        if (text.length > MAX_CHARS) {
            setError(`Message too long (${text.length}/${MAX_CHARS} chars).`);
            return;
        }

        setError('');
        setInput('');

        // Optimistically add user message
        const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMsg]);
        setLoading(true);

        try {
            const res = await axios.post(`${API_BASE}/chat/`, {
                message: text,
                thread_id: threadId || undefined,
                analysis_period: 'chat',
            });

            const data = res.data;
            if (!threadId) {
                setThreadId(data.thread_id);
                loadSessions(); // refresh session list
            }

            const agentMsg = {
                role: 'assistant',
                content: data.response || data.final_report || '(No response)',
                timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, agentMsg]);
        } catch (e) {
            const errText = e.response?.data?.error || 'Failed to reach the Investment Agent. Please try again.';
            setError(errText);
            // Remove the optimistic user message on failure
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop (click to close) */}
            <div style={styles.backdrop} onClick={onClose} aria-hidden="true" />

            {/* Panel */}
            <div style={styles.panel} role="dialog" aria-label="Investment Manager Chat" aria-modal="true">

                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <span style={styles.headerIcon}>📈</span>
                        <div>
                            <div style={styles.headerTitle}>Investment Manager</div>
                            <div style={styles.headerSub}>AI-powered · Advisory only · No trade execution</div>
                        </div>
                    </div>
                    <div style={styles.headerActions}>
                        <button
                            style={styles.iconBtn}
                            onClick={() => setShowSessions(s => !s)}
                            title="Recent conversations"
                            aria-label="Recent conversations"
                        >
                            🕐
                        </button>
                        <button
                            style={styles.iconBtn}
                            onClick={startNewConversation}
                            title="New conversation"
                            aria-label="New conversation"
                        >
                            ✏️
                        </button>
                        <button
                            style={styles.closeBtn}
                            onClick={onClose}
                            title="Close"
                            aria-label="Close chat panel"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Session list drawer */}
                {showSessions && (
                    <div style={styles.sessionList}>
                        <div style={styles.sessionListTitle}>Recent Conversations</div>
                        {sessions.length === 0 && (
                            <div style={styles.sessionEmpty}>No prior conversations yet.</div>
                        )}
                        {sessions.map(s => (
                            <button
                                key={s.thread_id}
                                style={styles.sessionItem}
                                onClick={() => loadHistory(s.thread_id)}
                            >
                                <span style={styles.sessionPeriod}>{periodLabel(s.analysis_period)}</span>
                                <span style={styles.sessionDate}>{formatTime(s.started_at)}</span>
                                <span style={{
                                    ...styles.sessionStatus,
                                    color: s.status === 'completed' ? '#4ade80' : '#facc15',
                                }}>
                                    {s.status}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Disclaimer banner */}
                <div style={styles.disclaimer}>
                    ⚠️ Advisory only — recommendations are not financial advice and no trades will be executed.
                </div>

                {/* Messages area */}
                <div style={styles.messagesArea}>
                    {loadingHistory && (
                        <div style={styles.centeredNote}>Loading conversation history…</div>
                    )}

                    {!loadingHistory && messages.length === 0 && (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>📊</div>
                            <div style={styles.emptyTitle}>Investment Manager Agent</div>
                            <div style={styles.emptyBody}>
                                Ask me about your portfolio strategy, investment themes, market trends,
                                or how your positions compare to your benchmark.
                            </div>
                            <div style={styles.emptyHints}>
                                <div style={styles.hint} onClick={() => setInput("What investment themes should I be focused on right now?")}>
                                    💡 What themes should I focus on?
                                </div>
                                <div style={styles.hint} onClick={() => setInput("How is my portfolio performing vs benchmark since 12/31/24?")}>
                                    📈 Portfolio vs benchmark performance
                                </div>
                                <div style={styles.hint} onClick={() => setInput("Help me develop my investment strategy for retirement in 25 years.")}>
                                    🎯 Develop my retirement strategy
                                </div>
                                <div style={styles.hint} onClick={() => setInput("What are the key macro risks I should be aware of right now?")}>
                                    🌐 Current macro risks
                                </div>
                            </div>
                        </div>
                    )}

                    {!loadingHistory && messages.map((msg, i) => (
                        <MessageBubble key={i} msg={msg} />
                    ))}

                    {loading && <TypingIndicator />}

                    <div ref={messagesEndRef} />
                </div>

                {/* Error bar */}
                {error && (
                    <div style={styles.errorBar}>
                        ⚠️ {error}
                        <button style={styles.errorDismiss} onClick={() => setError('')}>✕</button>
                    </div>
                )}

                {/* Input area */}
                <div style={styles.inputArea}>
                    <textarea
                        ref={inputRef}
                        style={styles.textarea}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about strategy, themes, portfolio analysis…"
                        rows={3}
                        maxLength={MAX_CHARS}
                        disabled={loading || loadingHistory}
                        aria-label="Message input"
                    />
                    <div style={styles.inputFooter}>
                        <span style={{
                            ...styles.charCount,
                            color: input.length > MAX_CHARS * 0.9 ? '#f87171' : '#6b7280',
                        }}>
                            {input.length}/{MAX_CHARS}
                        </span>
                        <span style={styles.inputHint}>Shift+Enter for new line</span>
                        <button
                            style={{
                                ...styles.sendBtn,
                                opacity: (!input.trim() || loading) ? 0.5 : 1,
                                cursor: (!input.trim() || loading) ? 'not-allowed' : 'pointer',
                            }}
                            onClick={sendMessage}
                            disabled={!input.trim() || loading}
                            aria-label="Send message"
                        >
                            {loading ? '⏳' : '➤ Send'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
    backdrop: {
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
    },
    panel: {
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: PANEL_WIDTH,
        background: '#1a1a2e',
        borderLeft: '1px solid #2d2d4e',
        display: 'flex', flexDirection: 'column',
        zIndex: 1001,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: '#e2e8f0',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px',
        background: '#16213e',
        borderBottom: '1px solid #2d2d4e',
        flexShrink: 0,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
    headerIcon: { fontSize: 24 },
    headerTitle: { fontWeight: 700, fontSize: 15, color: '#e2e8f0' },
    headerSub: { fontSize: 10, color: '#64748b', marginTop: 1 },
    headerActions: { display: 'flex', alignItems: 'center', gap: 6 },
    iconBtn: {
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 18, padding: '4px 6px', borderRadius: 6,
        color: '#94a3b8',
        transition: 'background 0.15s',
    },
    closeBtn: {
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 16, padding: '4px 8px', borderRadius: 6,
        color: '#94a3b8', fontWeight: 700,
    },
    sessionList: {
        background: '#0f172a',
        borderBottom: '1px solid #2d2d4e',
        maxHeight: 220,
        overflowY: 'auto',
        flexShrink: 0,
    },
    sessionListTitle: {
        padding: '8px 16px', fontSize: 11, fontWeight: 600,
        color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    sessionEmpty: { padding: '8px 16px', fontSize: 13, color: '#475569' },
    sessionItem: {
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 16px',
        background: 'none', border: 'none', cursor: 'pointer',
        textAlign: 'left', borderBottom: '1px solid #1e293b',
        color: '#cbd5e1',
        transition: 'background 0.15s',
    },
    sessionPeriod: { fontSize: 12, fontWeight: 600, color: '#60a5fa', minWidth: 60 },
    sessionDate: { fontSize: 11, color: '#64748b', flex: 1 },
    sessionStatus: { fontSize: 10, fontWeight: 600 },
    disclaimer: {
        padding: '6px 14px',
        background: '#1e1b4b',
        borderBottom: '1px solid #312e81',
        fontSize: 10, color: '#818cf8',
        flexShrink: 0,
    },
    messagesArea: {
        flex: 1, overflowY: 'auto',
        padding: '16px 12px',
        display: 'flex', flexDirection: 'column', gap: 4,
    },
    centeredNote: {
        textAlign: 'center', color: '#64748b', fontSize: 13, marginTop: 40,
    },
    emptyState: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '32px 20px', gap: 12,
    },
    emptyIcon: { fontSize: 40 },
    emptyTitle: { fontSize: 16, fontWeight: 700, color: '#e2e8f0' },
    emptyBody: { fontSize: 13, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 },
    emptyHints: { display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginTop: 8 },
    hint: {
        padding: '10px 14px',
        background: '#1e293b',
        borderRadius: 8,
        fontSize: 12, color: '#93c5fd',
        cursor: 'pointer',
        border: '1px solid #2d3748',
        transition: 'background 0.15s',
    },
    bubbleRow: {
        display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 8,
    },
    agentAvatar: {
        fontSize: 20, flexShrink: 0, marginBottom: 4,
    },
    userBubble: {
        maxWidth: '78%',
        background: '#1d4ed8',
        borderRadius: '16px 16px 4px 16px',
        padding: '10px 14px',
        fontSize: 13, color: '#e2e8f0', lineHeight: 1.5,
    },
    agentBubble: {
        maxWidth: '85%',
        background: '#1e293b',
        borderRadius: '16px 16px 16px 4px',
        padding: '12px 14px',
        fontSize: 13, color: '#e2e8f0', lineHeight: 1.6,
        border: '1px solid #2d3748',
    },
    bubbleTime: {
        fontSize: 10, color: '#475569', marginTop: 6, textAlign: 'right',
    },
    typingDot: {
        display: 'inline-block',
        width: 7, height: 7,
        borderRadius: '50%',
        background: '#60a5fa',
        margin: '0 2px',
        animation: 'bounce 1s infinite',
    },
    errorBar: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px',
        background: '#450a0a',
        borderTop: '1px solid #7f1d1d',
        fontSize: 12, color: '#fca5a5',
        flexShrink: 0,
    },
    errorDismiss: {
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#fca5a5', fontSize: 14, padding: '0 4px',
    },
    inputArea: {
        padding: '12px',
        borderTop: '1px solid #2d2d4e',
        background: '#16213e',
        flexShrink: 0,
    },
    textarea: {
        width: '100%',
        background: '#0f172a',
        border: '1px solid #334155',
        borderRadius: 8,
        color: '#e2e8f0',
        fontSize: 13,
        padding: '10px 12px',
        resize: 'none',
        outline: 'none',
        fontFamily: 'inherit',
        lineHeight: 1.5,
        boxSizing: 'border-box',
    },
    inputFooter: {
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
    },
    charCount: { fontSize: 11, minWidth: 60 },
    inputHint: { fontSize: 10, color: '#475569', flex: 1 },
    sendBtn: {
        background: '#1d4ed8',
        border: 'none',
        borderRadius: 8,
        color: '#fff',
        fontSize: 13, fontWeight: 600,
        padding: '8px 16px',
        cursor: 'pointer',
        transition: 'background 0.15s',
        whiteSpace: 'nowrap',
    },
    // Markdown styles
    mdH3: { fontSize: 15, fontWeight: 700, color: '#93c5fd', margin: '8px 0 4px' },
    mdH4: { fontSize: 13, fontWeight: 700, color: '#bfdbfe', margin: '6px 0 3px' },
    mdHr: { border: 'none', borderTop: '1px solid #334155', margin: '8px 0' },
    mdP: { margin: '2px 0', lineHeight: 1.6 },
    mdBullet: { display: 'flex', gap: 8, margin: '2px 0', alignItems: 'flex-start' },
    mdBulletDot: { color: '#60a5fa', flexShrink: 0, minWidth: 16, fontWeight: 700 },
    mdCode: {
        background: '#0f172a', borderRadius: 4,
        padding: '1px 5px', fontSize: 12,
        fontFamily: '"Fira Code", "Courier New", monospace',
        color: '#34d399',
    },
};
